import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Compass, MessageSquare, Users, ChevronRight, Clock, Target, CheckCircle2,
  Calendar, ArrowRight, TrendingUp, Sparkles,
} from "lucide-react";
import AppShell from "@/components/app/AppShell";
import StreakIcon from "@/components/app/StreakIcon";
import { IconAvatar } from "@/components/app/IconAvatar";
import { getDashboard } from "@/lib/api";
import CohortBanner from "@/components/app/CohortBanner";

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((response) => { if (!cancelled) setDashboard(response); })
      .catch(() => navigate("/auth"));
    return () => { cancelled = true; };
  }, [navigate]);

  if (!dashboard) return null;

  const { profile, stats, recentMatches: matches } = dashboard;
  const partnerships = dashboard.activePartnerships || (dashboard.activePartnership ? [dashboard.activePartnership] : []);
  const partnership = partnerships[0] || null;
  const partner = partnership?.partner?.profile || null;
  const todaysCheckin = partnership?.todaysCheckin;
  const streak = stats.streakDays;
  const checkinStreak = stats.checkinStreakDays || 0;
  const upcomingSessions = partnership?.upcomingSessions || [];
  const openTasks = partnership?.openTaskCount || 0;
  const doneTasks = partnership?.doneTaskCount || 0;

  const firstName = profile.fullName?.split(" ")[0] || "operator";

  return (
    <AppShell>
      <div className="px-6 sm:px-10 py-10 max-w-6xl mx-auto">
        {/* Hero row */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline mb-3">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
            <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
              Show up, <em className="italic text-[#F59E0B]">{firstName}.</em>
            </h1>
            <p className="mt-3 text-[#A1A1AA] font-light max-w-lg">
              {partnerships.length === 1
                ? `Day ${partnership.dayNumber} with ${partner?.fullName?.split(" ")[0]}. Consistency is the only shortcut.`
                : partnerships.length > 1
                ? `${partnerships.length} active partnerships. Consistency is the only shortcut - show up for each other.`
                : "You're not yet in a partnership. Discipline compounds faster when it's witnessed."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatPill label="Task streak" value={`${streak}d`} icon={StreakIcon} iconProps={{ lit: streak > 0 }} testId="dash-streak" />
            <StatPill label="Matches" value={matches.length} icon={Users} testId="dash-matches-count" />
            <StatPill label="Tasks" value={`${stats.doneTaskCount}/${stats.totalTaskCount}`} icon={CheckCircle2} testId="dash-tasks-stat" />
          </div>
        </div>

        <CohortBanner hideNext={profile.usageMode === "solo"} />

        {/* Main grid */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Active partnership OR CTA */}
          {partnerships.length > 1 ? (
            <div className="lg:col-span-2 hm-card p-6 sm:p-8 rounded-md" data-testid="dash-partnerships-card">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <div className="overline mb-1">Active partnerships</div>
                  <h2 className="font-serif-editorial text-2xl leading-tight">Your accountability circle</h2>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">{partnerships.length} active</span>
              </div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {partnerships.map((item) => {
                  const itemPartner = item.partner.profile;
                  return <div key={item.id} className="border border-white/10 bg-white/[0.02] p-4 rounded-md">
                    <div className="flex items-center gap-3 mb-4"><IconAvatar iconKey={itemPartner.profileIcon} size="md" /><div className="min-w-0"><div className="text-sm truncate">{itemPartner.fullName}</div><div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em]">Day {item.dayNumber}</div></div></div>
                    <div className="grid grid-cols-2 gap-2 mb-4"><MiniStat label="Today" value={`${item.doneTaskCount}/${item.totalTaskCount}`} sub="tasks" /><MiniStat label="Focus" value={`${Math.round(item.focusTotalMinutes / 60)}h`} sub={`${item.focusSessions} sessions`} /></div>
                    <Link to={`/workspace/${item.id}`} className="hm-btn-primary w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold">Open workspace <ArrowRight className="w-3.5 h-3.5" /></Link>
                  </div>;
                })}
              </div>
            </div>
          ) : partnership && partner ? (
            <div className="lg:col-span-2 hm-card p-6 sm:p-8 rounded-md" data-testid="dash-partnership-card">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <IconAvatar iconKey={partner.profileIcon} size="lg" />
                  <div>
                    <div className="overline mb-1">Active partnership</div>
                    <h2 className="font-serif-editorial text-2xl leading-tight">{partner.fullName}</h2>
                    <div className="text-xs text-[#A1A1AA] mt-1">@{partner.username} · {partner.city}, {partner.country}</div>
                  </div>
                </div>
                <Link to={`/workspace/${partnership.id}`} data-testid="dash-open-workspace" className="hm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold">
                  Open workspace <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <MiniStat label="Focus" value={`${Math.round(partnership.focusTotalMinutes / 60)}h`} sub={`${partnership.focusSessions} sessions`} />
                <MiniStat label="Check-ins" value={partnership.checkinCount} sub={`${partnership.checkinStreakDays ?? checkinStreak}d check-in streak`} />
                <MiniStat label="Open tasks" value={openTasks} sub={`${doneTasks} done`} />
              </div>

              <div className="p-4 rounded-md border border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Today's check-in</div>
                  {todaysCheckin ? (
                    <span className="text-[10px] text-[#F59E0B] uppercase tracking-[0.2em]">Submitted</span>
                  ) : (
                    <Link to={`/workspace/${partnership.id}?tab=checkin`} data-testid="dash-checkin-cta" className="text-[10px] text-[#F59E0B] hover:underline uppercase tracking-[0.2em]">Add now →</Link>
                  )}
                </div>
                {todaysCheckin ? (
                  <p className="text-sm text-[#F5F5F0] font-light">
                    <span className="text-[#A1A1AA]">Hours:</span> {todaysCheckin.hours}h · <span className="text-[#A1A1AA]">Wins:</span> {todaysCheckin.wins || <span className="italic text-[#7a7a80]">—</span>}
                  </p>
                ) : (
                  <p className="text-sm text-[#A1A1AA] font-light italic">Not logged yet. Your partner is waiting.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2 hm-card p-8 rounded-md relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#F59E0B]/10 blur-3xl pointer-events-none" />
              <div className="overline mb-3">No partnership yet</div>
              <h2 className="font-serif-editorial text-3xl leading-tight mb-3">Find someone who'll notice if you don't show up tomorrow.</h2>
              <p className="text-[#A1A1AA] font-light max-w-lg mb-6">
                Discover is curated on grit, cadence, and goal-fit. You have <span className="text-[#F5F5F0]">{matches.length} match{matches.length !== 1 && "es"}</span> waiting, or you can browse fresh candidates.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/discover" data-testid="dash-discover-cta" className="hm-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold">
                  <Compass className="w-4 h-4" /> Browse candidates
                </Link>
                {matches.length > 0 && (
                  <Link to="/matches" data-testid="dash-matches-cta" className="hm-btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm">
                    <Users className="w-4 h-4" /> See matches
                  </Link>
                )}
                <Link to="/matches#invite" data-testid="dash-invite-cta" className="hm-btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm">
                  Invite someone you know
                </Link>
              </div>
            </div>
          )}

          {/* Primary goal card */}
          <div className="hm-card p-6 rounded-md">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]">
                <Target className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="overline">Your primary goal</div>
            </div>
            <h3 className="font-serif-editorial text-xl leading-tight mb-2 line-clamp-2">{profile.goalDescription || "—"}</h3>
            <p className="text-xs text-[#A1A1AA] mb-4 line-clamp-2">
              {profile.target && <>Target: <span className="text-[#F5F5F0]">{profile.target}</span></>}
              {profile.targetDate && <> · by {profile.targetDate}</>}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {profile.goalTags?.slice(0, 4).map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-white/10 text-[#A1A1AA]">{t}</span>
              ))}
            </div>
            <Link to="/profile" className="mt-5 inline-flex items-center gap-1 text-xs text-[#F59E0B] hover:underline">
              Edit profile <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Sessions */}
          <div className="hm-card p-6 rounded-md" data-testid="dash-sessions">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-white/10 text-[#A1A1AA]">
                  <Calendar className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div className="overline">Upcoming sessions</div>
              </div>
              {partnership && <Link to={`/workspace/${partnership.id}?tab=sessions`} className="text-[10px] text-[#F59E0B] uppercase tracking-[0.2em]">Manage →</Link>}
            </div>
            {upcomingSessions.length === 0 ? (
              <p className="text-sm text-[#A1A1AA] font-light italic">No sessions scheduled.</p>
            ) : (
              <ul className="space-y-2">
                {upcomingSessions.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 text-sm">
                    <span className="font-serif-editorial text-[#F59E0B] w-14">{s.day}</span>
                    <span className="text-[#A1A1AA] w-24">{s.start}–{s.end}</span>
                    <span className="text-[#F5F5F0] font-light truncate">{s.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Momentum */}
          <div className="hm-card p-6 rounded-md">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-white/10 text-[#A1A1AA]">
                <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="overline">Momentum this week</div>
            </div>
            <MomentumBars momentum={dashboard.momentum || []} />
            <p className="text-xs text-[#A1A1AA] mt-4 font-light">
              {partnership
                ? "Each bar is one day's focused hours. Missing days are the enemy."
                : "Start a partnership and this graph fills up as you check in."}
            </p>
          </div>

          {/* Recent matches */}
          <div className="hm-card p-6 rounded-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-white/10 text-[#A1A1AA]">
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div className="overline">Recent matches</div>
              </div>
              <Link to="/matches" className="text-[10px] text-[#F59E0B] uppercase tracking-[0.2em]">All →</Link>
            </div>
            {matches.length === 0 ? (
              <p className="text-sm text-[#A1A1AA] font-light italic">No matches yet. Head to Discover or <Link to="/matches#invite" className="not-italic text-[#F59E0B] hover:underline">invite a partner</Link>.</p>
            ) : (
              <ul className="space-y-3">
                {matches.map((m) => {
                  const p = m.partner.profile;
                  return (
                    <li key={m.id} className="flex items-center gap-3">
                      <IconAvatar iconKey={p.profileIcon} size="sm" tone="outline" />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{p.fullName}</div>
                        <div className="text-[10px] text-[#A1A1AA] truncate">{p.goalTags.slice(0, 2).join(" · ")}</div>
                      </div>
                      <Link to={`/chat/${m.id}`} className="ml-auto text-[10px] text-[#F59E0B] uppercase tracking-[0.2em]"><MessageSquare className="w-3.5 h-3.5" /></Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function StatPill({ label, value, icon: Icon, iconProps, testId }) {
  return (
    <div className="hm-card px-4 py-3 rounded-md flex items-center gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#F59E0B]" strokeWidth={1.5} {...iconProps} />
      <div>
        <div className="font-serif-editorial text-xl leading-none">{value}</div>
        <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mt-0.5">{label}</div>
      </div>
    </div>
  );
}
function MiniStat({ label, value, sub }) {
  return (
    <div className="p-3 rounded-md border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em] mb-1">{label}</div>
      <div className="font-serif-editorial text-2xl leading-none">{value}</div>
      <div className="text-[10px] text-[#7a7a80] mt-1">{sub}</div>
    </div>
  );
}
function MomentumBars({ momentum }) {
  const days = momentum.map((day) => ({ date: day.date, hours: day.hours, label: day.label }));
  const max = Math.max(1, ...days.map((d) => d.hours));
  return (
    <div className="flex items-end gap-2 h-24">
      {days.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex-1 flex items-end">
            <div className="w-full rounded-sm" style={{ height: `${(d.hours / max) * 100}%`, minHeight: d.hours ? 4 : 0, background: d.hours ? "var(--hm-chart-accent)" : "transparent", border: d.hours ? "none" : "1px dashed rgba(255,255,255,0.06)" }} />
          </div>
          <span className="text-[10px] text-[#A1A1AA] uppercase">{d.label.slice(0, 1)}</span>
        </div>
      ))}
    </div>
  );
}
